import React, { useState, useEffect, useCallback } from 'react';
import { talukaAPI } from '../../services/api';

const STATUS_OPTIONS = ['', 'filed', 'acknowledged', 'assigned', 'in_progress', 'resolved', 'closed', 'rejected'];
const SEVERITY_OPTIONS = ['', 'low', 'medium', 'high', 'critical'];

function statusBadge(status) {
  return <span className={`tk-badge tk-badge-${status}`}>{status?.replace('_', ' ')}</span>;
}

function severityColor(s) {
  if (s === 'critical') return '#c62828';
  if (s === 'high')     return '#e65100';
  if (s === 'medium')   return '#f9a825';
  return '#2e7d32';
}

export default function TalukaComplaints() {
  const [complaints, setComplaints] = useState([]);
  const [gps, setGps]               = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedComplaint, setSelectedComplaint] = useState(null);

  const [filters, setFilters] = useState({
    tenant_id: '', status: '', severity: '', search: ''
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [cRes, gRes] = await Promise.all([
        talukaAPI.getComplaints({ page, limit: 20, ...filters }),
        gps.length === 0 ? talukaAPI.getGrampanchayats() : Promise.resolve({ data: { grampanchayats: gps } })
      ]);
      const cData = cRes.data?.complaints || cRes.data?.data || cRes.data || {};
      setComplaints(Array.isArray(cData) ? cData : []);
      setTotalPages(cRes.data?.pagination?.totalPages || Math.ceil((cRes.data?.pagination?.total || 1) / 20) || 1);
      if (gps.length === 0) setGps(gRes.data?.grampanchayats || gRes.data?.data || gRes.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load complaints');
    } finally {
      setLoading(false);
    }
  }, [page, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const setFilter = (key, val) => { setFilters(f => ({ ...f, [key]: val })); setPage(1); };

  return (
    <div>
      <div className="tk-page-header">
        <h1>Complaints</h1>
        <p>All complaints across Grampanchayats in your taluka</p>
      </div>

      <div className="tk-filter-bar">
        <input
          placeholder="Search title, ID…"
          value={filters.search}
          onChange={e => setFilter('search', e.target.value)}
        />
        <select value={filters.tenant_id} onChange={e => setFilter('tenant_id', e.target.value)}>
          <option value="">All GPs</option>
          {gps.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <select value={filters.status} onChange={e => setFilter('status', e.target.value)}>
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.filter(Boolean).map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select value={filters.severity} onChange={e => setFilter('severity', e.target.value)}>
          <option value="">All Severities</option>
          {SEVERITY_OPTIONS.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40 }}>Loading…</div>}
      {error   && <div style={{ textAlign: 'center', padding: 40, color: '#c62828' }}>{error}</div>}

      {!loading && !error && (
        <>
          <div className="tk-card">
            <div className="tk-table-wrap">
              <table className="tk-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Grampanchayat</th>
                    <th>Severity</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {complaints.map((c, i) => (
                    <tr key={c.id || i} onClick={() => setSelectedComplaint(c)} style={{ cursor: 'pointer' }}>
                      <td style={{ fontFamily: 'monospace', color: '#1565c0', whiteSpace: 'nowrap' }}>
                        {c.complaint_uid || c.id}
                      </td>
                      <td><strong>{c.title}</strong></td>
                      <td style={{ textTransform: 'capitalize' }}>{c.category}</td>
                      <td><strong>{c.grampanchayat?.name || c.gp_name || c.Tenant?.name || '—'}</strong></td>
                      <td>
                        <span style={{ color: severityColor(c.severity), fontWeight: 600, textTransform: 'capitalize' }}>
                          {c.severity}
                        </span>
                      </td>
                      <td>{statusBadge(c.status)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {c.createdAt ? new Date(c.createdAt).toLocaleDateString('en-IN') : '—'}
                      </td>
                    </tr>
                  ))}
                  {complaints.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#aaa' }}>No complaints found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="tk-pagination">
            <button onClick={() => setPage(p => p - 1)} disabled={page <= 1}>← Prev</button>
            <span style={{ fontSize: '0.85rem', color: '#555' }}>Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>Next →</button>
          </div>
        </>
      )}

      {/* Complaint Details Modal */}
      {selectedComplaint && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: 'white', borderRadius: 8, padding: 24, maxWidth: 600, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0 }}>Complaint Details</h2>
              <button onClick={() => setSelectedComplaint(null)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer' }}>×</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 10, fontWeight: 600, width: '40%' }}>Complaint ID:</td>
                  <td style={{ padding: 10, color: '#1565c0', fontFamily: 'monospace' }}>{selectedComplaint.complaint_uid || selectedComplaint.id}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 10, fontWeight: 600 }}>Title:</td>
                  <td style={{ padding: 10 }}><strong>{selectedComplaint.title}</strong></td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 10, fontWeight: 600 }}>Grampanchayat:</td>
                  <td style={{ padding: 10 }}>{selectedComplaint.grampanchayat?.name || selectedComplaint.gp_name || selectedComplaint.Tenant?.name || '—'}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 10, fontWeight: 600 }}>Category:</td>
                  <td style={{ padding: 10, textTransform: 'capitalize' }}>{selectedComplaint.category}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 10, fontWeight: 600 }}>Severity:</td>
                  <td style={{ padding: 10 }}><span style={{ color: severityColor(selectedComplaint.severity), fontWeight: 600, textTransform: 'capitalize' }}>{selectedComplaint.severity}</span></td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 10, fontWeight: 600 }}>Status:</td>
                  <td style={{ padding: 10 }}>{statusBadge(selectedComplaint.status)}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 10, fontWeight: 600 }}>Filed Date:</td>
                  <td style={{ padding: 10 }}>{selectedComplaint.createdAt ? new Date(selectedComplaint.createdAt).toLocaleString('en-IN') : '—'}</td>
                </tr>
                {selectedComplaint.description && (
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 10, fontWeight: 600, verticalAlign: 'top' }}>Description:</td>
                    <td style={{ padding: 10 }}>{selectedComplaint.description}</td>
                  </tr>
                )}
                {selectedComplaint.resolution_notes && (
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 10, fontWeight: 600, verticalAlign: 'top' }}>Resolution Notes:</td>
                    <td style={{ padding: 10 }}>{selectedComplaint.resolution_notes}</td>
                  </tr>
                )}
                {selectedComplaint.filedByUser && (
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 10, fontWeight: 600 }}>Filed By:</td>
                    <td style={{ padding: 10 }}>{selectedComplaint.filedByUser.full_name}</td>
                  </tr>
                )}
              </tbody>
            </table>
            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <button onClick={() => setSelectedComplaint(null)} style={{ padding: '8px 16px', background: '#1565c0', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
